import type { Profile, SpifPayoutType } from "@/api/client";
import { formatAudPrecise } from "@/lib/currency";

/**
 * Flat-rate deal registration bonus, in cents. Mirrors the backend's
 * SPIF_FLAT_RATE_CENTS (apps/api/src/prm_api/models/deal.py) — keep in sync
 * if the program rate ever changes.
 */
export const SPIF_FLAT_RATE_CENTS = 100_000;

/**
 * "$1,000.00" — AUD currency rendering used by the SPIF surfaces. Thin alias
 * over the consolidated {@link formatAudPrecise} (CHP-551); kept under this
 * name so the SPIF call-sites stay unchanged.
 */
export function formatAudCents(cents: number): string {
  return formatAudPrecise(cents);
}

/**
 * The firm's SPIF allocation rule, derived from the viewer's own profile.
 *
 * CHP-1175 closed the old backend gap: the payout setup now lives on the
 * Organization and the profile's flat `spif_opt_in` / `spif_payout_type`
 * fields are server-resolved from it — so every org member (BDMs included)
 * sees the firm's elected rule. Null remains possible (org-less partners,
 * or an org whose PO hasn't elected) — callers must render gracefully.
 */
export function spifPayoutType(profile: Profile | null | undefined): SpifPayoutType | null {
  if (!profile) return null;
  // Not opted in (or never answered) → treat as no payout setup.
  if (profile.spif_opt_in !== true) return null;
  return profile.spif_payout_type ?? null;
}

/** Allocation rule line for the SPIF tracker; null → omit the line. */
export const SPIF_ALLOCATION_RULE: Record<SpifPayoutType, string | null> = {
  direct: "Your firm's payout setup: SPIFs are paid directly to the registering BDM.",
  pooled: "Your firm's payout setup: SPIF value is pooled for your company to distribute.",
  none: null,
};
